import { z } from 'zod';
import { pick } from 'lodash';

import { isPipelineWithParent } from '@/core/pipeline';
import {
  Exploration,
  Metric,
  Model,
  Pipeline,
  PipelineStateRelation,
  RecordsLikeCell,
  Relation,
} from '@/explore/types';
import { getExplorationVariables, restoreInvalidOperation } from '@/explore/utils';
import { getFinalStateOrThrow, findRelationJoinKey } from '@/explore/pipeline/state';
import { getJoinKeys } from '@/explore/model/utils';
import { getCellByPipelineId } from '@/core/cell';
import { isRecursiveOperation } from '@/explore/edit-pipeline/utils';

const relationOptionValueSchema = z.union([
  // On This Page relation to block via model relation
  z.object({
    baseModelId: z.string(),
    key: z.string(),
    pipelineId: z.string(),
  }),
  //  Normal relation to model
  z.object({
    baseModelId: z.string(),
    key: z.string(),
  }),
  // Custom join to any block
  z.object({
    pipelineId: z.string(),
  }),
]);

export const constructRelationOptionValue = (
  reference: z.infer<typeof relationOptionValueSchema>,
) => JSON.stringify(pick(reference, 'baseModelId', 'key', 'pipelineId'));

export const deconstructRelationOptionValue = (value: string) => {
  return relationOptionValueSchema.parse(JSON.parse(value));
};

export const parseRelationOptionValue = (relations: PipelineStateRelation[], value: string) => {
  const reference = deconstructRelationOptionValue(value);
  if ('key' in reference) {
    const relation = relations.find(
      ({ baseModelId, key }) => key === reference.key && baseModelId === reference.baseModelId,
    );
    if (relation === undefined) {
      throw new Error(`Unable to find relation with key ${reference.key}`);
    }
    return {
      relation,
      ...('pipelineId' in reference ? { pipelineId: reference.pipelineId } : {}),
    };
  }
  return { pipelineId: reference.pipelineId, relation: undefined };
};

const getCellFinalState = (
  cell: RecordsLikeCell,
  exploration: Exploration,
  models: Model[],
  metrics: Metric[],
) => {
  return getFinalStateOrThrow(cell.pipeline, {
    models,
    variables: getExplorationVariables(exploration),
    metrics,
    exploration,
  });
};

export const pipelineContainsReference = (
  pipelineId: string,
  pipeline: Pipeline,
  exploration: Exploration,
): boolean => {
  return (
    (isPipelineWithParent(pipeline) && pipeline.parentId === pipelineId) ||
    pipeline.operations.some((item) => {
      const operation = restoreInvalidOperation(item);
      if (!isRecursiveOperation(operation) || operation.parameters.pipelineId === undefined) {
        return false;
      }

      if (operation.parameters.pipelineId === pipelineId) {
        return true;
      }

      const cell = getCellByPipelineId(exploration.view.cells, operation.parameters.pipelineId);

      return (
        cell !== undefined && pipelineContainsReference(pipelineId, cell.pipeline, exploration)
      );
    })
  );
};

/**
 * For a given cell get the matching relations for the current model.
 */
export const findRelationsForCell = (
  relations: PipelineStateRelation[],
  models: Model[],
  metrics: Metric[],
  cell: RecordsLikeCell,
  exploration: Exploration,
) => {
  const finalState = getCellFinalState(cell, exploration, models, metrics);
  // Downstream features are unable to handle hasOneThrough relations. Filtering them out here.
  // Including hasOneThrough relations caused SUP-4426.
  return relations
    .filter((relation) => relation.type !== 'hasOneThrough')
    .filter((relation) => {
      const joinKeys = getJoinKeys(relation, relations, models);
      return (
        findRelationJoinKey(finalState.fields, relation.modelId, joinKeys.joinKeyOnRelated) !==
        undefined
      );
    });
};

export const getRelationTypeLabel = (type: Relation['type']): string => {
  switch (type) {
    case 'hasOne':
    case 'hasOneThrough':
      return 'Has One';
    case 'hasMany':
      return 'One To Many';
    case 'manyToMany':
      return 'Many To Many';
    default:
      return type;
  }
};
